/**
 * A channel's threads beside it (2026-10-29; Discord's Threads list): the
 * active ones (joined first) from the store, the archived ones fetched page
 * by page, and the New thread form (from the header's Threads, a message's
 * "Create thread", or "See all threads"). On a phone it is the slot's full
 * pane under the header, on the back stack like pins and search.
 */
import {
  ChannelType,
  compareSnowflakes,
  snowflakeDate,
  THREAD_AUTO_ARCHIVE_DURATIONS,
  THREAD_NAME_MAX,
  threadNameFrom,
  type Channel,
  type Snowflake,
  type ThreadAutoArchiveDuration,
} from '@neosocial/protocol';
import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { useT } from '../i18n';
import { useData, useDataShallow } from '../store/data';
import { unreadOf } from '../store/selectors';
import { Btn, Icon, IconBtn, Seg, Select, Spinner, Switch } from '../ui/kit';
import { PreviewRow } from '../hub/Search';
import {
  closeThreadPanel,
  createThread,
  openThread,
  openThreadCreate,
  openThreadList,
  threadPerms,
  type ThreadPanelState,
} from './actions';
import { threadsApi } from './api';
import { threadsOf } from './store';

type Tab = 'active' | 'archived';

export function ThreadList({ channel, panel }: { channel: Channel; panel: ThreadPanelState }) {
  const { t } = useT();
  const p = useDataShallow((d) => threadPerms(d, channel));
  const canCreate = p.createPublic || p.createPrivate;
  const creating = panel.view === 'create';
  return (
    <div className="ns-thl slot-panel" role="region" aria-label={t('th.list.title')}>
      <div className="slot-h">
        {creating ? (
          <IconBtn
            icon="chev-left"
            label={t('th.create.cancel')}
            onClick={() => openThreadList(channel.id)}
          />
        ) : (
          <Icon name="thread" size="m" />
        )}
        <h2 className="slot-t">{t(creating ? 'th.create.title' : 'th.list.title')}</h2>
        {!creating && canCreate ? (
          <Btn kind="primary" size="xs" icon="plus" onClick={() => openThreadCreate(channel.id)}>
            {t('th.new')}
          </Btn>
        ) : null}
        <IconBtn icon="x" label={t('c.close')} onClick={closeThreadPanel} />
      </div>
      {creating ? (
        <ThreadCreate channel={channel} fromMessage={panel.fromMessage} />
      ) : (
        <Lists channel={channel} manage={p.manage} canCreate={canCreate} />
      )}
    </div>
  );
}

/* --------------------------------- lists --------------------------------- */

function Lists({
  channel,
  manage,
  canCreate,
}: {
  channel: Channel;
  manage: boolean;
  canCreate: boolean;
}) {
  const { t } = useT();
  const [tab, setTab] = useState<Tab>('active');
  // the store holds the active threads one sees (READY, then the thread events)
  const active = useDataShallow((d) =>
    threadsOf(d, channel.id).filter((c) => !c.thread_metadata?.archived),
  );
  const joined = active.filter((c) => !!c.member);
  const other = active.filter((c) => !c.member);
  return (
    <>
      <div className="ns-thl-tabs">
        <Seg<Tab>
          label={t('th.list.title')}
          value={tab}
          onChange={setTab}
          options={[
            { value: 'active', label: t('th.list.active') },
            { value: 'archived', label: t('th.list.archived') },
          ]}
        />
      </div>
      <div className="ns-thl-body nx-scroll">
        {tab === 'archived' ? (
          <Archived channelId={channel.id} manage={manage} />
        ) : active.length ? (
          <>
            {joined.length ? <Group label={t('th.row.joined')} list={joined} /> : null}
            {other.length ? (
              <Group label={t(joined.length ? 'th.list.other' : 'th.list.active')} list={other} />
            ) : null}
          </>
        ) : (
          <div className="slot-empty">
            <span className="slot-empty-ic" aria-hidden="true">
              <Icon name="thread" size="xl" />
            </span>
            <p>{t('th.list.none')}</p>
            {canCreate ? (
              <Btn
                kind="secondary"
                size="sm"
                icon="plus"
                onClick={() => openThreadCreate(channel.id)}
              >
                {t('th.new')}
              </Btn>
            ) : null}
          </div>
        )}
      </div>
    </>
  );
}

function Group({ label, list }: { label: string; list: Channel[] }) {
  return (
    <section className="ns-thl-g">
      <h3 className="ns-thl-gh">{label}</h3>
      <ul>
        {list.map((c) => (
          <ThreadRow key={c.id} c={c} />
        ))}
      </ul>
    </section>
  );
}

/** One thread in a list: its name, marks, how many messages, the last activity. */
export function ThreadRow({ c }: { c: Channel }) {
  const { t, fmt } = useT();
  const live = useData((d) => d.channels[c.id]) ?? c;
  const st = useDataShallow((d) => unreadOf(d, c.id));
  const meta = live.thread_metadata;
  const n = live.message_count ?? 0;
  const lastAt = live.last_message_id
    ? snowflakeDate(live.last_message_id)
    : (meta?.create_timestamp ?? snowflakeDate(live.id));
  const isPrivate = live.type === ChannelType.PRIVATE_THREAD;
  const unread = !!live.member && (st.unread || st.mentions > 0);
  return (
    <li>
      <button
        type="button"
        className="ns-thr"
        data-st={st.mentions && live.member ? 'mention' : unread ? 'unread' : undefined}
        aria-label={t('th.bar.open', { name: live.name ?? '' })}
        onClick={() => openThread(live)}
      >
        <span className="ns-thr-i" aria-hidden="true">
          <Icon name={isPrivate ? 'lock' : 'thread'} size="m" />
        </span>
        <span className="ns-thr-b">
          <span className="ns-thr-n" dir="auto">
            {live.name}
          </span>
          <span className="ns-thr-m">
            <span className="nx-num">{t('th.row.count', { n })}</span>
            <span aria-hidden="true">·</span>
            <span className="nx-num">{t('th.row.members', { n: live.member_count ?? 0 })}</span>
            <span aria-hidden="true">·</span>
            <time dateTime={new Date(lastAt).toISOString()}>{fmt.rel(lastAt)}</time>
          </span>
        </span>
        <span className="ns-thr-e">
          {isPrivate ? <span className="ns-th-chip">{t('th.row.private')}</span> : null}
          {meta?.locked ? (
            <span className="ns-th-chip">
              <Icon name="lock" size="s" />
              {t('th.row.locked')}
            </span>
          ) : null}
          {meta?.archived ? <span className="ns-th-chip">{t('th.row.archived')}</span> : null}
          {st.mentions && live.member ? <b className="nx-badge">{fmt.num(st.mentions)}</b> : null}
        </span>
      </button>
    </li>
  );
}

interface ArchivedPage {
  threads: Channel[];
  more: boolean;
}

/** A page of archived threads: public, and private (all with Manage Threads, else the joined ones). */
async function archivedPage(
  channelId: Snowflake,
  manage: boolean,
  before?: string,
): Promise<ArchivedPage> {
  const [pub, priv] = await Promise.all([
    threadsApi.archived(channelId, 'public', before),
    threadsApi.archived(channelId, manage ? 'private' : 'joined', before),
  ]);
  const mine = new Map([...pub.members, ...priv.members].map((m) => [m.id ?? '', m]));
  return {
    threads: [...pub.threads, ...priv.threads].map((c) =>
      mine.has(c.id) ? { ...c, member: mine.get(c.id) } : c,
    ),
    more: !!pub.has_more || !!priv.has_more,
  };
}

/** Archived threads: the public ones, and the private ones you may see (yours; all with Manage Threads). */
function Archived({ channelId, manage }: { channelId: Snowflake; manage: boolean }) {
  const { t } = useT();
  const [list, setList] = useState<Channel[] | null>(null);
  const [more, setMore] = useState(false);
  const [error, setError] = useState(false);
  const [busy, setBusy] = useState(false);
  /** One page (`before`: older than that archive time) put in the list, the newest archive first. */
  const show = useCallback((page: ArchivedPage, before?: string) => {
    setList((prev) => {
      const all = new Map([...(before ? (prev ?? []) : []), ...page.threads].map((c) => [c.id, c]));
      return [...all.values()].sort(
        (a, b) =>
          Date.parse(b.thread_metadata?.archive_timestamp ?? '0') -
            Date.parse(a.thread_metadata?.archive_timestamp ?? '0') ||
          compareSnowflakes(b.id, a.id),
      );
    });
    setMore(page.more);
    setError(false);
  }, []);
  useEffect(() => {
    let live = true;
    archivedPage(channelId, manage)
      .then((page) => live && show(page))
      .catch(() => live && setError(true));
    return () => {
      live = false;
    };
  }, [channelId, manage, show]);
  const load = async (before: string | undefined) => {
    setBusy(true);
    try {
      show(await archivedPage(channelId, manage, before), before);
    } catch {
      setError(true);
    } finally {
      setBusy(false);
    }
  };
  if (error && !list) return <p className="ns-muted slot-note">{t('th.list.error')}</p>;
  if (!list)
    return (
      <p className="ns-muted slot-note">
        <Spinner /> {t('ns.loading')}
      </p>
    );
  if (!list.length) return <p className="ns-muted slot-note">{t('th.list.noneArchived')}</p>;
  const oldest = list.at(-1)?.thread_metadata?.archive_timestamp;
  return (
    <>
      <ul className="ns-thl-g">
        {list.map((c) => (
          <ThreadRow key={c.id} c={c} />
        ))}
      </ul>
      {more ? (
        <Btn
          kind="ghost"
          size="sm"
          className="slot-more"
          busy={busy}
          onClick={() => void load(oldest)}
        >
          {t('th.list.more')}
        </Btn>
      ) : null}
    </>
  );
}

/* --------------------------------- create -------------------------------- */

/** The New thread form: from a message (public), or from the channel (public or private). */
export function ThreadCreate({
  channel,
  fromMessage,
}: {
  channel: Channel;
  fromMessage?: Snowflake;
}) {
  const { t } = useT();
  const p = useDataShallow((d) => threadPerms(d, channel));
  const msg = useData((d) => (fromMessage ? d.messages[channel.id]?.byId[fromMessage] : undefined));
  const [name, setName] = useState(() =>
    msg ? threadNameFrom(msg.content, t('th.create.title')) : '',
  );
  // a thread from a message is public (Discord); without one, private only when it is all you may start
  const canPrivate = !fromMessage && p.createPrivate;
  const [isPrivate, setPrivate] = useState(!fromMessage && !p.createPublic && p.createPrivate);
  const [archive, setArchive] = useState<ThreadAutoArchiveDuration>(4320);
  const [first, setFirst] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const v = name.trim();
    if (!v) {
      setError(t('th.create.nameRequired'));
      return;
    }
    setBusy(true);
    // a refusal is toasted and the form stays; a success opens the thread
    await createThread({
      channelId: channel.id,
      name: v,
      isPrivate: canPrivate && isPrivate,
      autoArchive: archive,
      fromMessage,
      first,
    });
    setBusy(false);
  };
  return (
    <form className="ns-form ns-thc nx-scroll" onSubmit={(e) => void submit(e)} noValidate>
      {msg ? (
        <div className="ns-field">
          <span className="ns-label">{t('th.create.fromMessage')}</span>
          <ul className="ns-thc-msg">
            <PreviewRow m={msg} close={() => undefined} onOpen={() => undefined} />
          </ul>
        </div>
      ) : null}
      <label className="ns-field">
        <span className="ns-label">{t('th.create.name')}</span>
        <input
          className="nx-input"
          dir="auto"
          value={name}
          maxLength={THREAD_NAME_MAX}
          placeholder={t('th.create.namePh')}
          aria-invalid={!!error || undefined}
          autoFocus
          onChange={(e) => {
            setName(e.target.value);
            setError(null);
          }}
        />
        {error ? (
          <span className="ns-err" role="alert">
            {error}
          </span>
        ) : null}
      </label>
      {!fromMessage ? (
        <div className="ns-row ns-thc-priv">
          <span className="ns-thc-priv-t">
            <span className="ns-thc-priv-n">
              <Icon name="lock" size="s" />
              {t('th.create.private')}
            </span>
            <span className="ns-help">
              {canPrivate ? t('th.create.privateSub') : t('th.create.privateNo')}
            </span>
          </span>
          <Switch
            checked={canPrivate && isPrivate}
            disabled={!canPrivate || !p.createPublic}
            label={t('th.create.private')}
            onChange={setPrivate}
          />
        </div>
      ) : null}
      <label className="ns-field">
        <span className="ns-label">{t('th.create.archive')}</span>
        <Select
          className="nx-input ns-select"
          value={String(archive)}
          onChange={(e) => setArchive(Number(e.target.value) as ThreadAutoArchiveDuration)}
        >
          {THREAD_AUTO_ARCHIVE_DURATIONS.map((m) => (
            <option key={m} value={String(m)}>
              {t(`th.archive.${m}`)}
            </option>
          ))}
        </Select>
      </label>
      <label className="ns-field">
        <span className="ns-label">{t('th.create.first')}</span>
        <textarea
          className="nx-textarea ns-thc-first"
          dir="auto"
          rows={3}
          value={first}
          placeholder={t('th.create.firstPh')}
          onChange={(e) => setFirst(e.target.value)}
        />
      </label>
      <div className="ns-row-btns end">
        <Btn kind="ghost" size="sm" onClick={() => openThreadList(channel.id)}>
          {t('th.create.cancel')}
        </Btn>
        <Btn kind="primary" size="sm" type="submit" busy={busy}>
          {t('th.create.submit')}
        </Btn>
      </div>
    </form>
  );
}
